import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getDomain } from "@/lib/academy.functions";
import { resolveEngine } from "@/academy/engines";
import type { InteractiveNode } from "@/academy/engines";
import { ArrowRight, Clock, Lock, Award } from "lucide-react";
import { useState } from "react";
import {
  ACADEMY_ID, NOINDEX_META, breadcrumbs, courseNode, ldScript, seoMeta,
} from "@/lib/schema";
import { absoluteUrl } from "@/lib/site";

export const Route = createFileRoute("/academy/$domain/")({
  loader: async ({ params }) => {
    const data = await getDomain({ data: { slug: params.domain } });
    if (!data) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Domain unavailable — Buddha KA Academy" }, NOINDEX_META] };
    }
    const d = loaderData.domain;
    const path = `/academy/${params.domain}`;
    const title = `${d.name} — Buddha KA Academy`;
    const description =
      d.description ?? d.tagline ?? `${d.name}: a domain of study inside the Buddha KA Academy.`;
    return {
      ...seoMeta({ title, description, path }),
      scripts: [
        ldScript(
          courseNode({
            id: `${absoluteUrl(path)}#course`,
            url: absoluteUrl(path),
            name: d.name,
            description,
            partOfCourseId: ACADEMY_ID,
            lessons: loaderData.paths.map((p) => ({
              path: `/academy/${params.domain}/${p.slug}`,
              name: p.title,
            })),
          }),
        ),
        ldScript(breadcrumbs([["Academy", "/academy"], [d.name, path]])),
      ],
    };
  },

  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl">Domain not found</h1>
      <Link to="/academy" className="mt-4 inline-block text-gold">Back to Academy</Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <p className="text-sm text-foreground/60">{(error as Error).message}</p>
    </div>
  ),
  component: DomainPage,
});

function DomainPage() {
  const initial = Route.useLoaderData();
  const { data } = useQuery({
    queryKey: ["academy", "domain", initial.domain.slug],
    queryFn: () => getDomain({ data: { slug: initial.domain.slug } }),
    initialData: initial,
  });
  const [selected, setSelected] = useState<InteractiveNode | null>(null);
  if (!data) return null;
  const { domain, paths, nodes, certifications } = data;
  const engine = resolveEngine((domain as { engine_key?: string | null }).engine_key ?? null);

  return (
    <div className="min-h-dvh bg-obsidian text-foreground">
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">{domain.tagline}</p>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-gold-luminous">{domain.name}</h1>
        <p className="mt-4 max-w-2xl text-foreground/75">{domain.description}</p>
      </section>

      <section className="mx-auto grid max-w-6xl gap-8 px-4 pb-16 sm:px-6 lg:grid-cols-[400px_1fr]">
        <div>
          <h2 className="font-serif text-2xl text-gold-luminous">Explore</h2>
          <p className="mt-2 text-sm text-foreground/70">Tap a node to open its lesson.</p>
          <div className="mt-4">
            <engine.Hero
              domain={domain as never}
              nodes={nodes as InteractiveNode[]}
              onSelect={(n) => setSelected(n)}
              selectedKey={selected?.key ?? null}
            />
          </div>
          {selected?.lesson_id && (
            <Link
              to="/academy/$domain/lesson/$lesson"
              params={{ domain: domain.slug, lesson: selected.lesson_id }}
              className="mt-3 inline-flex items-center gap-2 text-sm text-gold hover:underline"
            >
              Open lesson <ArrowRight size={14} />
            </Link>
          )}
        </div>

        <div>
          <h2 className="font-serif text-2xl text-gold-luminous">Learning Paths</h2>
          <div className="mt-4 grid gap-4">
            {paths.map((p: typeof paths[number]) => (
              <Link key={p.id} to="/academy/$domain/$path" params={{ domain: domain.slug, path: p.slug }}
                className="group flex items-start justify-between gap-4 rounded-xl border hairline p-5 transition hover:border-gold/60">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-serif text-lg">{p.title}</h3>
                    <span className="rounded-full border hairline px-2 py-0.5 text-[10px] uppercase tracking-widest text-foreground/60">
                      {p.difficulty}
                    </span>
                    {p.tier === "premium" && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-gold/50 px-2 py-0.5 text-[10px] uppercase tracking-widest text-gold">
                        <Lock size={10} /> Premium
                      </span>
                    )}
                  </div>
                  {p.subtitle && <p className="mt-1 text-sm uppercase tracking-widest text-foreground/60">{p.subtitle}</p>}
                  {p.description && <p className="mt-2 text-sm text-foreground/75">{p.description}</p>}
                  {p.est_minutes ? (
                    <p className="mt-2 flex items-center gap-1 text-xs text-foreground/50">
                      <Clock size={12} /> {p.est_minutes} min
                    </p>
                  ) : null}
                </div>
                <ArrowRight size={18} className="mt-1 shrink-0 text-gold group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>

          {certifications.length > 0 && (
            <div className="mt-10">
              <h2 className="font-serif text-2xl text-gold-luminous">Certifications</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {certifications.map((c: typeof certifications[number]) => (
                  <div key={c.id} className="rounded-xl border hairline p-4">
                    <div className="flex items-center gap-2 text-gold">
                      <Award size={16} /> <span className="font-serif">{c.title}</span>
                    </div>
                    {c.description && <p className="mt-2 text-sm text-foreground/70">{c.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
